import type { Nullable } from "entities/common";

/** Пол */
export enum GENDER {
  /** Мужской */
  MAN = 1,
  /** Женский */
  WOMAN = 2,
}

/** Признак подтвержденной эл. почты */
export enum EMAIL_VERIFIED {
  /** Подтвержден */
  VERIFIED = 1,
  /** Не подтвержден */
  NOT_VERIFIED = 0,
}

/** Модель адреса */
export interface IAddressModel {
  /** Единая строка адреса */
  addressName: string;
}

/** Модель идентификатора для запроса на информацию о профиле клиента */
export type IClientProfileIdsModel = {
  /** Идентификатор клиента во внешней системе */
  extID: string;
  /** Код внешней системы */
  system: "FANSY-STD" | "CR-CRM";
};

/** Модель запроса на информацию о профиле клиента */
export interface IClientProfileRequest {
  /** Идентификаторы клиента в других системах */
  ids: IClientProfileIdsModel[];
}

/** Запрос на подтверждение электронной почты */
export type IClientsVerifyEmailRequest = IClientProfileRequest;

/** Запрос на подтверждение кода */
export interface IClientsVerifyCodeRequest extends IClientProfileRequest {
  /** Код подтверждения */
  code: string;
}

/** Возможные статусы анкеты */
export enum FORM_STATUS {
  /** Новая */
  NEW = 0,
  /** Проверка сведений */
  CHECKING = 1,
  /** Действует */
  VALID = 2,
  /** Отклонена */
  REJECTED = 4,
  /** Устарела */
  OUTDATED = 8,
}

/** Коды статуса договора */
export enum AGREEMENT_STATUS {
  /** Оформление */
  NEW = 10,
  /** Подписан */
  SIGNED = 20,
  /** Действует */
  VALID = 30,
  /** Блокировка */
  BLOCKED = 40,
  /** Расторжение */
  REJECTED = 50,
}

/** Коды типов договоров */
export enum AGREEMENT_TYPE {
  /** Инвестиционный договор ДУ */
  DU = 0,
  /** Инвестиционный договор ИИС */
  OLD_IIS = 3,
  /** Инвестиционный договор ИИС-3 */
  IIS = 4,
}

/** Модель договора в профиле клиента */
export interface IAgreement {
  /** id договора в CRM */
  crmId: string;
  /** id договора в Fansy */
  contractId: number | null;
  /** Тип договора 0-ДУ 3 - ИИС 4 - ИИС-3 */
  /** Не отображать в блоке "Ваши заявления", если type равен 3 или 4, а status: 10. */
  type: number;
  /** Номер договора */
  name: string;
  /** Дата договора */
  date: string;
  /** Полное наименование текущего продукта договора */
  strategy: string;
  /** Статус договора
   * 10 - Оформление
   * 20 - Подписан
   * 30 - Действует
   * 40 - Блокировка
   * 50 - Расторжение
   */
  status: AGREEMENT_STATUS;
  /** Инвест-профиль договора */
  profile?: {
    /** Идентификатор инвест-профиля в CRM*/
    profileCrmId: string;
    /**
     * Код статуса и порядковый номер состояния в статусной
     * модели инвестиционного профиля.
     */
    status: number;
    /** Валюта инвестирования */
    currency?: string;
    /**  Инвестиционный горизонт*/
    horizon?: string;
    /** Допустимый уровень риска в % годовых */
    riskRate?: string;
    /** Ожидаемая доходность в % годовых */
    expectedYield?: string;
    /** Наименование уровня риска Низкий Умеренный Высокий Максимальный */
    riskName: string;
    /** Дата истечения инвестиционного профиля */
    expirationDate?: string;
  } | null;
}

/** Модель ответа на информацию о профиле клиента */
export interface IClientProfile {
  /** Имя */
  firstName: string;
  /** Фамилия */
  lastName: string;
  /** Отчество */
  middleName: string;
  /** Дата рождения */
  birthDate: Nullable<string>;
  /** Место рождения */
  birthPlace: string;
  /** Страна рождения */
  birthCountry: string;
  /** Гражданство */
  citizenships: string;
  /** ИНН */
  inn: string;
  /** Снилс */
  snils: string;
  /** Пол */
  gender: GENDER;
  /** Код, сгенерированный в CRM */
  crmPublicCode: string;
  /** Дата регистрации */
  regDate: string;
  /** Документ удостоверяющий личность */
  personDocument: {
    /** Код документа */
    code: string;
    /** Серия */
    series: string;
    /** Номер */
    number: string;
    /** Дата выдачи */
    issueDate: Nullable<string>;
    /** Кем выдан */
    issuedBy: string;
    /** Код подразделения */
    issueId: string;
  };
  /** Номер телефона */
  mobile: string;
  /** Электронная почта */
  email: string;
  /** Признак верификации почты */
  emailVerified: EMAIL_VERIFIED;
  /** Адрес проживания */
  liveAddress?: IAddressModel;
  /** Адрес регистрации */
  registerAddress?: IAddressModel;
  /** Почтовый адрес */
  postAddress?: IAddressModel;
  /** Состояние анкеты */
  formStatus?: FORM_STATUS;
  /** Статус инвестора */
  investorStatus?: number;
  /** Массив текущих и будущих договоров */
  agreements?: IAgreement[];
}
